import { useState, useEffect, useRef } from "react";
import {
  collection, addDoc, getDocs, query,
  where, orderBy, doc, deleteDoc, updateDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { auth, db, storage } from "../firebase";
import { formatDateLong } from "../utils/formatDate";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from "react-simple-maps";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const COUNTRY_NAMES = {
  "004": "Afghanistan", "008": "Albania", "012": "Algeria", "024": "Angola",
  "028": "Antigua and Barbuda", "032": "Argentina", "036": "Australia", "040": "Austria",
  "044": "Bahamas", "050": "Bangladesh", "052": "Barbados", "056": "Belgium",
  "060": "Bermuda", "064": "Bhutan", "068": "Bolivia", "076": "Brazil",
  "092": "British Virgin Islands", "100": "Bulgaria", "104": "Myanmar", "116": "Cambodia",
  "120": "Cameroon", "124": "Canada", "136": "Cayman Islands", "144": "Sri Lanka",
  "152": "Chile", "156": "China", "158": "Taiwan", "170": "Colombia",
  "188": "Costa Rica", "191": "Croatia", "192": "Cuba", "196": "Cyprus",
  "203": "Czech Republic", "208": "Denmark", "212": "Dominica", "214": "Dominican Republic",
  "218": "Ecuador", "222": "El Salvador", "231": "Ethiopia", "246": "Finland",
  "250": "France", "276": "Germany", "288": "Ghana", "300": "Greece",
  "308": "Grenada", "312": "Guadeloupe", "320": "Guatemala", "332": "Haiti",
  "340": "Honduras", "348": "Hungary", "356": "India", "360": "Indonesia",
  "364": "Iran", "368": "Iraq", "372": "Ireland", "376": "Israel",
  "380": "Italy", "388": "Jamaica", "392": "Japan", "398": "Kazakhstan",
  "400": "Jordan", "404": "Kenya", "408": "North Korea", "410": "South Korea",
  "414": "Kuwait", "418": "Laos", "422": "Lebanon", "434": "Libya",
  "440": "Lithuania", "442": "Luxembourg", "458": "Malaysia", "474": "Martinique",
  "484": "Mexico", "500": "Montserrat", "504": "Morocco", "508": "Mozambique",
  "524": "Nepal", "528": "Netherlands", "531": "Curaçao", "533": "Aruba",
  "534": "Sint Maarten", "535": "Bonaire", "554": "New Zealand", "566": "Nigeria",
  "578": "Norway", "586": "Pakistan", "591": "Panama", "598": "Papua New Guinea",
  "604": "Peru", "608": "Philippines", "616": "Poland", "620": "Portugal",
  "630": "Puerto Rico", "634": "Qatar", "642": "Romania", "643": "Russia",
  "652": "Saint Barthélemy", "659": "Saint Kitts and Nevis", "660": "Anguilla",
  "662": "Saint Lucia", "663": "Saint Martin", "670": "Saint Vincent and the Grenadines",
  "682": "Saudi Arabia", "686": "Senegal", "694": "Sierra Leone", "703": "Slovakia",
  "705": "Slovenia", "706": "Somalia", "710": "South Africa", "716": "Zimbabwe",
  "724": "Spain", "729": "Sudan", "752": "Sweden", "756": "Switzerland",
  "760": "Syria", "762": "Tajikistan", "764": "Thailand", "780": "Trinidad and Tobago",
  "784": "United Arab Emirates", "792": "Turkey", "796": "Turks and Caicos Islands",
  "800": "Uganda", "804": "Ukraine", "818": "Egypt", "826": "United Kingdom",
  "840": "United States", "850": "US Virgin Islands", "858": "Uruguay",
  "860": "Uzbekistan", "862": "Venezuela", "704": "Vietnam",
  "887": "Yemen", "894": "Zambia",
};

// Sub-territories without separate ISO 3166-1 codes — searchable and scratchable
const TERRITORY_NAMES = {
  "territory-saba": "Saba",
  "territory-sint-eustatius": "Sint Eustatius",
  "territory-tobago": "Tobago",
  "territory-nevis": "Nevis",
};

const ALL_SEARCHABLE = [
  ...Object.entries(COUNTRY_NAMES).map(([code, name]) => ({ code, name, isTerritory: false })),
  ...Object.entries(TERRITORY_NAMES).map(([code, name]) => ({ code, name, isTerritory: true })),
].sort((a, b) => a.name.localeCompare(b.name));

const getPlaceName = (code) => COUNTRY_NAMES[code] || TERRITORY_NAMES[code] || code;

const TOTAL_COUNTRIES = 195;
const CONTINENT_MAP = {
  NA: [
    "124","484","840","192","214","332","340","320","222","188","591","630",
    "044","052","780","028","662","308","670","212","659","533","136","796",
    "092","850","474","312","531","534","535","652","660","500","663","060",
  ],
  SA: ["076","032","152","170","604","068","858","862","218","600"],
  EU: ["826","250","276","380","724","620","528","056","756","040","616","203","752","578","208","246","300","191","348","642","804","440","442","703","705","372","100","008"],
  AF: ["818","504","566","710","404","231","288","024","508","686","120","706","800","729","716","694","012","434"],
  AS: ["643","792","356","156","392","410","764","704","360","458","608","702","784","682","376","400","364","368","050","586","524","064","144","104","116","418","408","760","887","398","860","762"],
  OC: ["036","554","598"],
};

const MOODS = ["😊 Happy", "😍 Amazed", "😌 Relaxed", "🤩 Excited", "😴 Tired", "😅 Adventurous", "🥰 In love", "🤔 Reflective"];

function getStats(visited) {
  const pct = ((visited.length / TOTAL_COUNTRIES) * 100).toFixed(1);
  const continentsVisited = Object.entries(CONTINENT_MAP).filter(([, codes]) =>
    codes.some(c => visited.includes(c))
  ).length;
  let milestone = "";
  if (visited.length === 0) milestone = "Tap a country to get started!";
  else if (visited.length < 10) milestone = `${10 - visited.length} more to reach 10 countries 🌱`;
  else if (visited.length < 25) milestone = `${25 - visited.length} more to reach 25 countries ✈️`;
  else if (visited.length < 50) milestone = `${50 - visited.length} more to reach 50 countries 🌍`;
  else if (visited.length < 100) milestone = `${100 - visited.length} more to reach 100 countries 🏆`;
  else milestone = "World traveler! 🌟";
  return { pct, continentsVisited, milestone };
}

export default function TravelJournal({ onBack }) {
  const [activeTab, setActiveTab] = useState("journal");
  const [entries, setEntries] = useState([]);
  const [visitedCountries, setVisitedCountries] = useState([]);
  const [vcDocId, setVcDocId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [tooltipContent, setTooltipContent] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [position, setPosition] = useState({ coordinates: [0, 20], zoom: 1 });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [tappedLabel, setTappedLabel] = useState(null);
  const tapTimerRef = useRef(null);
  const searchRef = useRef(null);
  const [form, setForm] = useState({
    title: "",
    body: "",
    location: "",
    mood: "",
    date: new Date().toISOString().split("T")[0],
    isPublic: false,
  });
  const [detailEntry, setDetailEntry] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchResults = searchQuery.trim().length > 0
    ? ALL_SEARCHABLE
        .filter(({ name }) => name.toLowerCase().includes(searchQuery.toLowerCase()))
        .slice(0, 10)
    : [];

  const showTapLabel = (name, wasVisited) => {
    setTappedLabel({ name, added: !wasVisited });
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    tapTimerRef.current = setTimeout(() => setTappedLabel(null), 2000);
  };

  const handleTap = (numericCode, countryName) => {
    const wasVisited = visitedCountries.includes(numericCode);
    toggleCountry(numericCode);
    showTapLabel(countryName || numericCode, wasVisited);
  };

  const handleSelectFromSearch = (code) => {
    const wasVisited = visitedCountries.includes(code);
    toggleCountry(code);
    showTapLabel(getPlaceName(code), wasVisited);
    setSearchQuery("");
    setSearchOpen(false);
  };

  const fetchData = async () => {
    if (!auth.currentUser) { setLoading(false); return; }
    setLoading(true);
    setFetchError(null);
    try {
      // If orderBy is added here alongside where(), a Firestore composite index is required
      const q = query(
        collection(db, "journalEntries"),
        where("authorId", "==", auth.currentUser.uid),
      );
      const snap = await getDocs(q);
      const sorted = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const tsA = a.createdAt?.toMillis?.() ?? (a.date ? Date.parse(a.date) : 0);
          const tsB = b.createdAt?.toMillis?.() ?? (b.date ? Date.parse(b.date) : 0);
          return tsB - tsA;
        });
      setEntries(sorted);

      const vcSnap = await getDocs(
        query(collection(db, "visitedCountries"), where("uid", "==", auth.currentUser.uid))
      );
      if (!vcSnap.empty) {
        setVisitedCountries(vcSnap.docs[0].data().countries || []);
        setVcDocId(vcSnap.docs[0].id);
      }
    } catch (err) {
      console.error(err);
      setFetchError("Failed to load journal entries. Please try again.");
    }
    setLoading(false);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const resetFormState = () => {
    setForm({ title: "", body: "", location: "", mood: "", date: new Date().toISOString().split("T")[0], isPublic: false });
    setPhotoFile(null);
    setPhotoPreview(null);
    setEditingEntry(null);
    setShowForm(false);
  };

  const openEdit = (entry) => {
    setForm({
      title: entry.title || "",
      body: entry.body || "",
      location: entry.location || "",
      mood: entry.mood || "",
      date: entry.date || new Date().toISOString().split("T")[0],
      isPublic: !!entry.isPublic,
    });
    setPhotoFile(null);
    setPhotoPreview(entry.photoURL || null);
    setEditingEntry(entry);
    setShowForm(true);
  };

  const deleteStoragePhoto = async (entry) => {
    if (!entry?.photoURL && !entry?.photoPath) return;
    try {
      let path = entry.photoPath;
      if (!path && entry.photoURL) {
        const start = entry.photoURL.indexOf('/o/') + 3;
        const end = entry.photoURL.indexOf('?');
        path = decodeURIComponent(entry.photoURL.slice(start, end));
      }
      await deleteObject(ref(storage, path));
    } catch (err) {
      if (err?.code !== 'storage/object-not-found') {
        console.warn('[deleteStoragePhoto] failed:', err);
      }
    }
  };

  const handleSubmit = async () => {
    if (!form.title || !form.body) return;
    if (!auth.currentUser) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const uid = auth.currentUser.uid;

      if (editingEntry) {
        // ── EDIT MODE ────────────────────────────────────────────────────────
        const update = { ...form };

        if (photoFile) {
          // New photo selected — upload, then best-effort delete old
          const photoPath = `journalPhotos/${uid}/${Date.now()}`;
          await uploadBytes(ref(storage, photoPath), photoFile);
          update.photoURL = await getDownloadURL(ref(storage, photoPath));
          update.photoPath = photoPath;
          deleteStoragePhoto(editingEntry); // fire-and-forget
        } else if (!photoPreview) {
          // Photo removed — null out fields, best-effort delete old
          update.photoURL = null;
          update.photoPath = null;
          deleteStoragePhoto(editingEntry); // fire-and-forget
        }
        // else: photoPreview set + no new file → photo kept; omit photo fields from update

        await updateDoc(doc(db, "journalEntries", editingEntry.id), update);
        setEntries(prev => prev.map(e => e.id === editingEntry.id ? { ...e, ...update } : e));
      } else {
        // ── CREATE MODE ───────────────────────────────────────────────────────
        let photoURL = null;
        let photoPath = null;
        if (photoFile) {
          photoPath = `journalPhotos/${uid}/${Date.now()}`;
          await uploadBytes(ref(storage, photoPath), photoFile);
          photoURL = await getDownloadURL(ref(storage, photoPath));
        }
        const docRef = await addDoc(collection(db, "journalEntries"), {
          ...form, photoURL, photoPath,
          authorId: uid,
          createdAt: new Date(),
        });
        const newEntry = { id: docRef.id, ...form, photoURL, photoPath, authorId: uid, createdAt: new Date() };
        setEntries(prev => [newEntry, ...prev]);
      }

      resetFormState();
    } catch (err) {
      console.error(err);
      setSubmitError(editingEntry ? "Failed to save changes. Please try again." : "Failed to save entry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = async (entry, onSuccess) => {
    if (!window.confirm("Delete this journal entry? This can't be undone.")) return;
    const deleted = entries.find(e => e.id === entry.id);
    setEntries(prev => prev.filter(e => e.id !== entry.id));
    try {
      await deleteDoc(doc(db, "journalEntries", entry.id));
    } catch (err) {
      console.error(err);
      if (deleted) setEntries(prev => [deleted, ...prev.filter(e => e.id !== entry.id)]);
      setSubmitError("Failed to delete entry. Please try again.");
      return;
    }
    if (onSuccess) onSuccess();
    deleteStoragePhoto(entry); // fire-and-forget
  };

  const handleToggleVisibility = async (entry) => {
    const newIsPublic = !entry.isPublic;
    setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, isPublic: newIsPublic } : e));
    try {
      await updateDoc(doc(db, "journalEntries", entry.id), { isPublic: newIsPublic });
    } catch (err) {
      console.error(err);
      setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, isPublic: entry.isPublic } : e));
      setSubmitError("Couldn't update visibility. Please try again.");
    }
  };

  const toggleCountry = async (numericCode) => {
    if (!auth.currentUser) return;
    const newVisited = visitedCountries.includes(numericCode)
      ? visitedCountries.filter(c => c !== numericCode)
      : [...visitedCountries, numericCode];
    setVisitedCountries(newVisited);

    try {
      if (vcDocId) {
        const { updateDoc } = await import("firebase/firestore");
        await updateDoc(doc(db, "visitedCountries", vcDocId), { countries: newVisited });
      } else {
        const newDoc = await addDoc(collection(db, "visitedCountries"), {
          uid: auth.currentUser.uid,
          countries: newVisited
        });
        setVcDocId(newDoc.id);
      }
    } catch (err) {
      console.error(err);
      setSubmitError("Couldn't update your scratch map. Please try again.");
    }
  };

  const { pct, continentsVisited, milestone } = getStats(visitedCountries);
  const progress = Math.min((visitedCountries.length / TOTAL_COUNTRIES) * 100, 100);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .tj-root { min-height: 100vh; background: #FAF6F0; font-family: 'DM Sans', sans-serif; padding-bottom: 40px; }
        .tj-nav { background: white; border-bottom: 0.5px solid rgba(26,23,20,0.08); padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 100; }
        .tj-back { font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.55); cursor: pointer; background: none; border: none; font-family: 'DM Sans', sans-serif; }
        .tj-back:hover { color: #F5A623; }
        .tj-nav-title { font-family: 'Playfair Display', serif; font-size: 16px; font-weight: 600; color: #1a1714; }
        .tj-add-btn { font-size: 12px; font-weight: 500; color: white; background: #F5A623; border: none; border-radius: 100px; padding: 7px 16px; cursor: pointer; font-family: 'DM Sans', sans-serif; }

        .tj-tabs { display: flex; background: white; border-bottom: 0.5px solid rgba(26,23,20,0.08); }
        .tj-tab { flex: 1; padding: 14px; text-align: center; font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.45); cursor: pointer; border: none; background: none; font-family: 'DM Sans', sans-serif; border-bottom: 2px solid transparent; transition: all 0.2s; }
        .tj-tab.active { color: #F5A623; border-bottom-color: #F5A623; }

        .tj-content { padding: 16px; }

        .tj-empty { background: white; border-radius: 16px; padding: 40px 24px; text-align: center; border: 1.5px dashed rgba(26,23,20,0.12); }
        .tj-empty-icon { font-size: 36px; margin-bottom: 12px; }
        .tj-empty-title { font-size: 16px; font-weight: 500; color: #1a1714; margin-bottom: 6px; }
        .tj-empty-sub { font-size: 13px; color: rgba(26,23,20,0.45); margin-bottom: 20px; line-height: 1.5; }
        .tj-empty-btn { padding: 10px 20px; background: #F5A623; color: white; border: none; border-radius: 100px; font-size: 13px; font-weight: 500; cursor: pointer; font-family: 'DM Sans', sans-serif; }

        .tj-entry { background: white; border-radius: 16px; border: 0.5px solid rgba(26,23,20,0.08); overflow: hidden; margin-bottom: 14px; }
        .tj-entry-photo { width: 100%; height: 180px; object-fit: cover; }
        .tj-entry-body { padding: 16px; }
        .tj-entry-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 6px; }
        .tj-entry-meta { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 8px; }
        .tj-entry-date { font-size: 11px; color: rgba(26,23,20,0.4); }
        .tj-entry-location { font-size: 11px; color: rgba(26,23,20,0.4); }
        .tj-entry-mood { font-size: 11px; background: #e3eef6; color: #F5A623; padding: 2px 8px; border-radius: 100px; }
        .tj-entry-title { font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 600; color: #1a1714; margin-bottom: 8px; }
        .tj-entry-text { font-size: 13px; color: rgba(26,23,20,0.65); line-height: 1.65; font-weight: 300; }
        .tj-delete-btn { background: none; border: none; font-size: 14px; color: rgba(26,23,20,0.25); cursor: pointer; }
        .tj-delete-btn:hover { color: #c9622f; }

        /* COUNTRY SEARCH */
        .tj-search-wrap { position: relative; margin-bottom: 12px; }
        .tj-search-input { width: 100%; padding: 12px 16px 12px 40px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 12px; background: white; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; transition: border-color 0.2s; }
        .tj-search-input:focus { border-color: #F5A623; }
        .tj-search-input::placeholder { color: rgba(26,23,20,0.3); }
        .tj-search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: rgba(26,23,20,0.35); font-size: 15px; pointer-events: none; }
        .tj-search-dropdown { position: absolute; top: calc(100% + 4px); left: 0; right: 0; background: white; border: 1.5px solid rgba(26,23,20,0.1); border-radius: 12px; box-shadow: 0 8px 24px rgba(26,23,20,0.12); z-index: 50; overflow: hidden; }
        .tj-search-option { padding: 11px 16px; font-size: 14px; color: #1a1714; cursor: pointer; display: flex; justify-content: space-between; align-items: center; transition: background 0.1s; }
        .tj-search-option:hover { background: #FAF6F0; }
        .tj-search-visited-badge { font-size: 10px; background: #e3eef6; color: #F5A623; border-radius: 100px; padding: 2px 8px; font-weight: 500; }
        .tj-search-territory-badge { font-size: 10px; background: #FAF6F0; color: rgba(26,23,20,0.45); border-radius: 100px; padding: 2px 8px; font-weight: 400; }
        .tj-search-no-results { padding: 12px 16px; font-size: 13px; color: rgba(26,23,20,0.4); }

        /* MAP */
        .tj-stats-bar { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 12px; }
        .tj-stat-card { background: white; border-radius: 14px; padding: 14px 10px; text-align: center; border: 0.5px solid rgba(26,23,20,0.08); }
        .tj-stat-num { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 600; color: #F5A623; margin-bottom: 2px; }
        .tj-stat-label { font-size: 10px; color: rgba(26,23,20,0.45); font-weight: 300; }
        .tj-milestone { background: #1E2D4A; border-radius: 12px; padding: 12px 16px; margin-bottom: 12px; display: flex; align-items: center; gap: 10px; }
        .tj-milestone-text { font-size: 13px; color: rgba(255,255,255,0.8); flex: 1; }
        .tj-progress-wrap { height: 5px; background: rgba(255,255,255,0.1); border-radius: 3px; margin-top: 6px; overflow: hidden; }
        .tj-progress-fill { height: 5px; background: #7BA7C4; border-radius: 3px; transition: width 0.5s ease; }

        .tj-map-card { background: #1E2D4A; border-radius: 16px; overflow: hidden; margin-bottom: 12px; }
        .tj-map-header { padding: 14px 16px 0; display: flex; justify-content: space-between; align-items: center; }
        .tj-map-title { font-family: 'Playfair Display', serif; font-size: 15px; font-weight: 600; color: white; }
        .tj-map-hint { font-size: 11px; color: rgba(255,255,255,0.4); }
        .tj-map-zoom { display: flex; gap: 6px; }
        .tj-zoom-btn { width: 28px; height: 28px; border-radius: 8px; background: rgba(255,255,255,0.1); border: none; color: white; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .tj-tooltip { background: white; border-radius: 8px; padding: 6px 10px; font-size: 12px; font-weight: 500; color: #1a1714; position: fixed; pointer-events: none; z-index: 999; box-shadow: 0 4px 16px rgba(0,0,0,0.15); }

        .tj-visited-list { background: white; border-radius: 16px; padding: 16px; margin-bottom: 12px; }
        .tj-visited-title { font-size: 12px; font-weight: 500; color: rgba(26,23,20,0.4); margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.06em; }
        .tj-visited-tags { display: flex; flex-wrap: wrap; gap: 6px; }
        .tj-visited-tag { font-size: 12px; padding: 4px 12px; border-radius: 100px; background: #e3eef6; color: #F5A623; font-weight: 500; cursor: pointer; transition: background 0.15s; }
        .tj-visited-tag:hover { background: #fef0ed; color: #c9622f; }

        /* MODAL */
        .tj-overlay { position: fixed; inset: 0; background: rgba(26,23,20,0.5); z-index: 200; display: flex; align-items: flex-end; justify-content: center; }
        .tj-modal { background: white; border-radius: 20px 20px 0 0; padding: 28px 24px; width: 100%; max-width: 600px; max-height: 90vh; overflow-y: auto; animation: slideUp 0.3s ease; }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .tj-modal-title { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 600; color: #1a1714; margin-bottom: 20px; }
        .tj-field { margin-bottom: 14px; }
        .tj-label { display: block; font-size: 12px; font-weight: 500; color: rgba(26,23,20,0.55); margin-bottom: 5px; }
        .tj-input { width: 100%; padding: 12px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 10px; background: #fafaf8; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; }
        .tj-input:focus { border-color: #F5A623; background: white; }
        .tj-textarea { width: 100%; padding: 12px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 10px; background: #fafaf8; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; resize: none; height: 110px; }
        .tj-textarea:focus { border-color: #F5A623; background: white; }
        .tj-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .tj-moods { display: flex; flex-wrap: wrap; gap: 6px; }
        .tj-mood-btn { padding: 6px 12px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 100px; background: white; font-size: 12px; cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
        .tj-mood-btn.selected { background: #e3eef6; border-color: #7BA7C4; color: #F5A623; }
        .tj-photo-area { border: 2px dashed rgba(26,23,20,0.15); border-radius: 12px; padding: 20px; text-align: center; cursor: pointer; margin-bottom: 14px; }
        .tj-photo-preview { width: 100%; height: 150px; object-fit: cover; border-radius: 8px; margin-bottom: 8px; }
        .tj-submit-btn { width: 100%; padding: 14px; background: #F5A623; color: white; border: none; border-radius: 12px; font-size: 15px; font-weight: 500; font-family: 'DM Sans', sans-serif; cursor: pointer; margin-top: 8px; }
        .tj-cancel-btn { width: 100%; padding: 12px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 12px; background: white; font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.5); cursor: pointer; font-family: 'DM Sans', sans-serif; margin-top: 8px; }
        .tj-vis-btn { background: none; border: 1px solid rgba(26,23,20,0.12); border-radius: 100px; padding: 2px 8px; font-size: 13px; cursor: pointer; line-height: 1.4; transition: background 0.15s; }
        .tj-vis-btn:hover { background: rgba(26,23,20,0.05); }
        .tj-vis-toggle-row { display: flex; gap: 8px; }
        .tj-vis-opt { padding: 7px 16px; border-radius: 100px; font-size: 12px; font-weight: 500; border: 1.5px solid rgba(26,23,20,0.12); background: white; color: rgba(26,23,20,0.5); cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
        .tj-vis-opt.active { background: #1E2D4A; color: white; border-color: #1E2D4A; }

        /* DETAIL MODAL */
        .tj-detail-modal { background: white; border-radius: 20px 20px 0 0; padding: 28px 24px; width: 100%; max-width: 600px; max-height: 90vh; overflow-y: auto; animation: slideUp 0.3s ease; position: relative; }
        .tj-detail-close { position: absolute; top: 16px; right: 20px; background: none; border: none; font-size: 22px; color: rgba(26,23,20,0.4); cursor: pointer; line-height: 1; }
        .tj-detail-close:hover { color: #1E2D4A; }
        .tj-detail-mood { font-size: 52px; text-align: center; margin-bottom: 12px; }
        .tj-detail-title { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 600; color: #1E2D4A; text-align: center; margin-bottom: 12px; line-height: 1.3; }
        .tj-detail-meta { display: flex; gap: 8px; justify-content: center; align-items: center; flex-wrap: wrap; margin-bottom: 20px; }
        .tj-detail-badge { font-size: 12px; padding: 3px 10px; border-radius: 100px; background: #e3eef6; color: #F5A623; font-weight: 500; }
        .tj-detail-photos { display: flex; gap: 10px; overflow-x: auto; margin-bottom: 20px; padding-bottom: 4px; }
        .tj-detail-photo { height: 200px; border-radius: 12px; flex-shrink: 0; object-fit: cover; }
        .tj-detail-body { font-size: 14px; color: rgba(26,23,20,0.7); line-height: 1.75; font-weight: 300; white-space: pre-wrap; margin-bottom: 20px; }
        .tj-detail-trip { font-size: 12px; color: rgba(26,23,20,0.5); background: #FAF6F0; border-radius: 8px; padding: 8px 12px; display: inline-flex; align-items: center; gap: 6px; }
      `}</style>

      <div className="tj-root">
        <div className="tj-nav">
          <button className="tj-back" onClick={onBack}>← Back</button>
          <span className="tj-nav-title">Travel Journal</span>
          {activeTab === "journal" && (
            <button className="tj-add-btn" onClick={() => setShowForm(true)}>+ New entry</button>
          )}
          {activeTab === "map" && <div style={{width:80}}></div>}
        </div>

        <div className="tj-tabs">
          <button className={`tj-tab ${activeTab === "journal" ? "active" : ""}`} onClick={() => setActiveTab("journal")}>
            📓 Journal
          </button>
          <button className={`tj-tab ${activeTab === "map" ? "active" : ""}`} onClick={() => setActiveTab("map")}>
            🗺️ Scratch Map
          </button>
        </div>

        <div className="tj-content">
          {activeTab === "journal" && (
            <>
              {fetchError && (
                <p style={{fontSize:13,color:'#c9622f',paddingTop:12}}>{fetchError}</p>
              )}
              {loading ? (
                <p style={{textAlign:'center',color:'rgba(26,23,20,0.4)',fontSize:14,paddingTop:20}}>Loading...</p>
              ) : entries.length === 0 ? (
                <div className="tj-empty">
                  <div className="tj-empty-icon">📓</div>
                  <div className="tj-empty-title">Your journal is empty</div>
                  <div className="tj-empty-sub">Start logging your travel memories — moments, moods, photos and stories from the road.</div>
                  <button className="tj-empty-btn" onClick={() => setShowForm(true)}>Write your first entry</button>
                </div>
              ) : (
                entries.map(entry => (
                  <div key={entry.id} className="tj-entry" onClick={() => setDetailEntry(entry)} style={{cursor:'pointer'}}>
                    {entry.photoURL && <img src={entry.photoURL} alt="" className="tj-entry-photo" />}
                    <div className="tj-entry-body">
                      <div className="tj-entry-top">
                        <div className="tj-entry-meta">
                          {entry.date && <span className="tj-entry-date">📅 {formatDateLong(entry.date)}</span>}
                          {entry.location && <span className="tj-entry-location">📍 {entry.location}</span>}
                          {entry.mood && <span className="tj-entry-mood">{entry.mood}</span>}
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                          <button className="tj-delete-btn" onClick={e => { e.stopPropagation(); handleDeleteEntry(entry); }}>×</button>
                        </div>
                      </div>
                      <div className="tj-entry-title">{entry.title}</div>
                      <div className="tj-entry-text">{entry.body}</div>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === "map" && (
            <>
              <div className="tj-stats-bar">
                <div className="tj-stat-card">
                  <div className="tj-stat-num">{visitedCountries.length}</div>
                  <div className="tj-stat-label">Countries</div>
                </div>
                <div className="tj-stat-card">
                  <div className="tj-stat-num">{pct}%</div>
                  <div className="tj-stat-label">World explored</div>
                </div>
                <div className="tj-stat-card">
                  <div className="tj-stat-num">{continentsVisited}/7</div>
                  <div className="tj-stat-label">Continents</div>
                </div>
              </div>

              <div className="tj-milestone">
                <span style={{fontSize:18}}>🎯</span>
                <div style={{flex:1}}>
                  <div className="tj-milestone-text">{milestone}</div>
                  <div className="tj-progress-wrap">
                    <div className="tj-progress-fill" style={{width:`${progress}%`}}></div>
                  </div>
                </div>
              </div>

              <div className="tj-search-wrap" ref={searchRef}>
                <span className="tj-search-icon">🔍</span>
                <input
                  className="tj-search-input"
                  placeholder="Search for a country..."
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                  onFocus={() => setSearchOpen(true)}
                />
                {searchOpen && searchQuery.trim().length > 0 && (
                  <div className="tj-search-dropdown">
                    {searchResults.length === 0 ? (
                      <div className="tj-search-no-results">No countries found</div>
                    ) : searchResults.map(({ code, name, isTerritory }) => (
                      <div key={code} className="tj-search-option" onClick={() => handleSelectFromSearch(code)}>
                        <span>{name}</span>
                        <div style={{display:'flex',gap:5,alignItems:'center'}}>
                          {isTerritory && <span className="tj-search-territory-badge">territory</span>}
                          {visitedCountries.includes(code) && <span className="tj-search-visited-badge">✓ visited</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="tj-map-card">
                <div className="tj-map-header">
                  <div className="tj-map-title">My world map</div>
                  <div className="tj-map-hint">Tap or search ✨</div>
                </div>

                <ComposableMap
                  projectionConfig={{ scale: 120, center: [0, 20] }}
                  style={{ width: "100%", height: "520px", background: "#1E2D4A" }}
                >
                  <ZoomableGroup
                    zoom={position.zoom}
                    center={position.coordinates}
                    onMoveEnd={setPosition}
                    minZoom={1}
                    maxZoom={6}
                  >
                    <Geographies geography={GEO_URL}>
                      {({ geographies }) =>
                        geographies.map((geo) => {
                          const numericCode = geo.id;
                          const isVisited = visitedCountries.includes(String(numericCode));
                          const countryName = COUNTRY_NAMES[String(numericCode)] || "";
                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              onClick={() => handleTap(String(numericCode), countryName)}
                              onMouseEnter={() => setTooltipContent(countryName)}
                              onMouseLeave={() => setTooltipContent("")}
                              style={{
                                default: {
                                  fill: isVisited ? "#F5A623" : "rgba(255,255,255,0.12)",
                                  stroke: "#1E2D4A",
                                  strokeWidth: 0.5,
                                  outline: "none",
                                  cursor: "pointer",
                                  transition: "fill 0.2s",
                                },
                                hover: {
                                  fill: isVisited ? "#C47A0A" : "rgba(255,255,255,0.3)",
                                  stroke: "#1E2D4A",
                                  strokeWidth: 0.5,
                                  outline: "none",
                                  cursor: "pointer",
                                },
                                pressed: {
                                  fill: "#1D9E75",
                                  outline: "none",
                                },
                              }}
                            />
                          );
                        })
                      }
                    </Geographies>
                  </ZoomableGroup>
                </ComposableMap>

                {(tappedLabel || tooltipContent) && (
                  <div style={{padding:'8px 16px',background:'rgba(255,255,255,0.1)',borderTop:'0.5px solid rgba(255,255,255,0.1)'}}>
                    <span style={{fontSize:12,color:'rgba(255,255,255,0.8)',fontWeight:500}}>
                      {tappedLabel
                        ? (tappedLabel.added ? `✓ ${tappedLabel.name} added` : `× ${tappedLabel.name} removed`)
                        : `📍 ${tooltipContent}`}
                    </span>
                  </div>
                )}
              </div>

              {visitedCountries.length > 0 && (
                <div className="tj-visited-list">
                  <div className="tj-visited-title">{visitedCountries.length} countries visited</div>
                  <div className="tj-visited-tags">
                    {visitedCountries.map(code => (
                      <span key={code} className="tj-visited-tag" onClick={() => toggleCountry(code)}
                        title="Tap to remove">
                        {getPlaceName(code)} ×
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {visitedCountries.length === 0 && (
                <div className="tj-empty">
                  <div className="tj-empty-icon">🌍</div>
                  <div className="tj-empty-title">Start scratching!</div>
                  <div className="tj-empty-sub">Tap any country on the map to mark it as visited.</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {detailEntry && (
        <div className="tj-overlay" onClick={e => e.target === e.currentTarget && setDetailEntry(null)}>
          <div className="tj-detail-modal">
            <button className="tj-detail-close" onClick={() => setDetailEntry(null)}>×</button>
            <button
              onClick={() => handleDeleteEntry(detailEntry, () => setDetailEntry(null))}
              style={{position:'absolute',top:16,right:52,background:'none',border:'none',fontSize:16,color:'rgba(26,23,20,0.4)',cursor:'pointer',lineHeight:1,padding:4}}
              title="Delete entry"
            >🗑</button>
            <button
              onClick={() => { openEdit(detailEntry); setDetailEntry(null); }}
              style={{position:'absolute',top:16,right:84,background:'none',border:'none',fontSize:16,color:'rgba(26,23,20,0.4)',cursor:'pointer',lineHeight:1,padding:4}}
              title="Edit entry"
            >✏️</button>
            {detailEntry.mood && (
              <div>
                <div className="tj-detail-mood">{detailEntry.mood.split(' ')[0]}</div>
                <div style={{textAlign:'center',fontSize:13,fontWeight:500,color:'rgba(26,23,20,0.55)',marginTop:-4,marginBottom:16}}>
                  {detailEntry.mood.split(' ').slice(1).join(' ')}
                </div>
              </div>
            )}
            <div className="tj-detail-title">{detailEntry.title}</div>
            <div className="tj-detail-meta">
              {detailEntry.date && <span className="tj-entry-date">📅 {formatDateLong(detailEntry.date)}</span>}
              {detailEntry.location && <span className="tj-entry-location">📍 {detailEntry.location}</span>}
            </div>
            {detailEntry.photoURL && (
              <div className="tj-detail-photos">
                <img src={detailEntry.photoURL} alt="" className="tj-detail-photo" />
              </div>
            )}
            <div className="tj-detail-body">{detailEntry.body}</div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="tj-overlay" onClick={e => e.target === e.currentTarget && resetFormState()}>
          <div className="tj-modal">
            <div className="tj-modal-title">{editingEntry ? "Edit entry 📓" : "New journal entry 📓"}</div>

            <div className="tj-photo-area" onClick={() => document.getElementById('journal-photo').click()}>
              {photoPreview ? (
                <div style={{position:'relative',display:'inline-block',width:'100%'}}>
                  <img src={photoPreview} alt="" className="tj-photo-preview" />
                  <button
                    onClick={e => { e.stopPropagation(); setPhotoFile(null); setPhotoPreview(null); }}
                    style={{position:'absolute',top:6,right:6,background:'rgba(26,23,20,0.55)',border:'none',borderRadius:'50%',width:24,height:24,color:'white',cursor:'pointer',fontSize:14,lineHeight:'24px',textAlign:'center',padding:0}}
                    title="Remove photo"
                  >×</button>
                </div>
              ) : (
                <>
                  <div style={{fontSize:24,marginBottom:6}}>📷</div>
                  <div style={{fontSize:13,color:'rgba(26,23,20,0.45)'}}>Add a photo — optional</div>
                </>
              )}
              <input id="journal-photo" type="file" accept="image/*" style={{display:'none'}} onChange={handlePhotoChange} />
            </div>

            <div className="tj-field">
              <label className="tj-label">Entry title</label>
              <input className="tj-input" placeholder="e.g. Sunrise at Angkor Wat" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
            </div>

            <div className="tj-row">
              <div className="tj-field">
                <label className="tj-label">Location</label>
                <input className="tj-input" placeholder="City or place" value={form.location} onChange={e => setForm({...form, location: e.target.value})} />
              </div>
              <div className="tj-field">
                <label className="tj-label">Date</label>
                <input className="tj-input" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
              </div>
            </div>

            <div className="tj-field">
              <label className="tj-label">Mood</label>
              <div className="tj-moods">
                {MOODS.map(mood => (
                  <button key={mood} className={`tj-mood-btn ${form.mood === mood ? "selected" : ""}`}
                    onClick={() => setForm({...form, mood: form.mood === mood ? "" : mood})}>
                    {mood}
                  </button>
                ))}
              </div>
            </div>

            <div className="tj-field">
              <label className="tj-label">Your story</label>
              <textarea className="tj-textarea" placeholder="Write about your day, what you saw, how you felt..." value={form.body} onChange={e => setForm({...form, body: e.target.value})} />
            </div>

            {submitError &&<p style={{fontSize:13, color:'#c9622f', marginTop:8}}>{submitError}</p>}
            <button className="tj-submit-btn" onClick={handleSubmit} disabled={submitting || !form.title || !form.body}>
              {submitting ? "Saving..." : editingEntry ? "Save changes →" : "Save entry →"}
            </button>
            <button className="tj-cancel-btn" onClick={resetFormState}>Cancel</button>
          </div>
        </div>
      )}
    </>
  );
}